import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CalendarOff, Clock } from "lucide-react";

const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export function ShiftsTab({ team, canWrite }) {
  const staff = team.filter((t) => t.bookable_internal === true || t.bookable_online === true || ["stylist", "assistant", "manager", "reception"].includes(t.role));
  const [operatorId, setOperatorId] = useState(staff[0]?.id || "");
  const [templates, setTemplates] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [draft, setDraft] = useState({ weekday: "0", start_hm: "09:00", end_hm: "18:00", break_start_hm: "", break_end_hm: "" });
  const [exc, setExc] = useState({ date: "", type: "off", start_hm: "09:00", end_hm: "13:00" });

  const load = useCallback(async () => {
    if (!operatorId) return;
    const { data } = await api.get(`/team/shifts?operator_id=${operatorId}`);
    setTemplates(data.templates); setExceptions(data.exceptions);
  }, [operatorId]);
  useEffect(() => { load(); }, [load]);

  const addTemplate = async () => {
    try { await api.post("/team/shifts", { operator_id: operatorId, ...draft, weekday: Number(draft.weekday) });
      toast.success("Turno aggiunto"); load(); }
    catch (e) { toast.error("Errore"); }
  };
  const del = async (id) => { try { await api.delete(`/team/shifts/${id}`); load(); } catch (e) { toast.error("Errore"); } };
  const addExc = async () => {
    if (!exc.date) { toast.error("Scegli una data"); return; }
    try { await api.post("/team/shift-exceptions", { operator_id: operatorId, ...exc }); toast.success("Eccezione salvata"); load(); }
    catch (e) { toast.error("Errore"); }
  };
  const delExc = async (id) => { try { await api.delete(`/team/shift-exceptions/${id}`); load(); } catch (e) { toast.error("Errore"); } };

  const byDay = WEEKDAYS.map((_, i) => templates.filter((t) => t.weekday === i));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Label className="shrink-0">Collaboratore</Label>
        <Select value={operatorId} onValueChange={setOperatorId}>
          <SelectTrigger className="w-[240px]" data-testid="shifts-operator"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{staff.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="p-4">
        <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4" /> Turni settimanali ricorrenti</h3>
        <div className="grid gap-2">
          {byDay.map((rows, i) => (
            <div key={i} className="flex items-start gap-3 py-1.5 border-b border-border/50 last:border-0">
              <div className="w-24 text-sm font-medium pt-1.5">{WEEKDAYS[i]}</div>
              <div className="flex-1 flex flex-wrap gap-2">
                {rows.length === 0 && <span className="text-xs text-muted-foreground pt-1.5">Riposo</span>}
                {rows.map((t) => (
                  <Badge key={t.id} variant="secondary" className="text-xs py-1 gap-1.5" data-testid={`shift-badge-${t.id}`}>
                    {t.start_hm}–{t.end_hm}{t.break_start_hm ? ` (pausa ${t.break_start_hm}-${t.break_end_hm})` : ""}
                    {canWrite && <button onClick={() => del(t.id)} className="ml-1 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2 mt-4 pt-3 border-t border-border">
            <div className="space-y-1"><Label className="text-xs">Giorno</Label>
              <Select value={draft.weekday} onValueChange={(v) => setDraft({ ...draft, weekday: v })}><SelectTrigger className="w-[130px]" data-testid="shift-weekday"><SelectValue /></SelectTrigger><SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Inizio</Label><Input type="time" value={draft.start_hm} onChange={(e) => setDraft({ ...draft, start_hm: e.target.value })} className="w-[110px]" /></div>
            <div className="space-y-1"><Label className="text-xs">Fine</Label><Input type="time" value={draft.end_hm} onChange={(e) => setDraft({ ...draft, end_hm: e.target.value })} className="w-[110px]" /></div>
            <div className="space-y-1"><Label className="text-xs">Pausa da</Label><Input type="time" value={draft.break_start_hm} onChange={(e) => setDraft({ ...draft, break_start_hm: e.target.value })} className="w-[110px]" /></div>
            <div className="space-y-1"><Label className="text-xs">Pausa a</Label><Input type="time" value={draft.break_end_hm} onChange={(e) => setDraft({ ...draft, break_end_hm: e.target.value })} className="w-[110px]" /></div>
            <Button onClick={addTemplate} data-testid="shift-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><CalendarOff className="h-4 w-4" /> Eccezioni e override per data</h3>
        <p className="text-xs text-muted-foreground mb-3">Giorno di riposo straordinario oppure orario personalizzato per una data specifica (override).</p>
        <div className="space-y-2 mb-4">
          {exceptions.length === 0 && <p className="text-sm text-muted-foreground">Nessuna eccezione.</p>}
          {exceptions.map((e) => (
            <div key={e.id} className="flex items-center gap-3 text-sm" data-testid={`shift-exc-${e.id}`}>
              <span className="font-medium w-28">{e.date}</span>
              <Badge variant={e.type === "off" ? "outline" : "secondary"} className="text-[10px]">{e.type === "off" ? "Riposo" : `${e.start_hm}–${e.end_hm}`}</Badge>
              {e.note && <span className="text-xs text-muted-foreground">{e.note}</span>}
              {canWrite && <button onClick={() => delExc(e.id)} className="ml-auto hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-border">
            <div className="space-y-1"><Label className="text-xs">Data</Label><Input type="date" value={exc.date} onChange={(e) => setExc({ ...exc, date: e.target.value })} className="w-[150px]" data-testid="exc-date" /></div>
            <div className="space-y-1"><Label className="text-xs">Tipo</Label><Select value={exc.type} onValueChange={(v) => setExc({ ...exc, type: v })}><SelectTrigger className="w-[150px]" data-testid="exc-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="off">Riposo</SelectItem><SelectItem value="custom">Orario custom</SelectItem></SelectContent></Select></div>
            {exc.type === "custom" && <>
              <div className="space-y-1"><Label className="text-xs">Inizio</Label><Input type="time" value={exc.start_hm} onChange={(e) => setExc({ ...exc, start_hm: e.target.value })} className="w-[110px]" /></div>
              <div className="space-y-1"><Label className="text-xs">Fine</Label><Input type="time" value={exc.end_hm} onChange={(e) => setExc({ ...exc, end_hm: e.target.value })} className="w-[110px]" /></div>
            </>}
            <Button variant="outline" onClick={addExc} data-testid="exc-add"><Plus className="h-4 w-4 mr-1" /> Salva eccezione</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
